import { ExternalLink, FilePenLine } from 'lucide-react';
import type { BoardFaceLinkCardSurface } from '@molt/shared';
import { Button } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface LinkCardChromeProps {
  readonly card: BoardFaceLinkCardSurface;
  readonly onConvert?: (() => void) | undefined;
}

function hostLabel(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.hostname.replace(/^www\./, '') || parsed.pathname || url;
  } catch {
    return url;
  }
}

/**
 * Whether a preview line would only repeat what the card's own title slot is
 * already showing, one element away on the same card front.
 *
 * ---------------------------------------------------------------------------
 * ⚠ WHY THIS IS A STRING COMPARISON AND NOT A PROP
 * ---------------------------------------------------------------------------
 * `CardTile` renders `<LinkCardChrome>` and, below it, the card's own `title` —
 * two slots, fed from two places that arrive at the same characters in two of
 * the three link-card states:
 *
 * - **unavailable**: `apps/server/src/routes/board-contents.ts` overrides the
 *   surface's `title` with its `UNAVAILABLE_LINK_TARGET_TITLE` literal, and
 *   this component draws `boardDashboard.link.unavailableTitle` from the
 *   catalogue. Two independent sources, the same four syllables, stacked.
 * - **external**: `POST /cards` writes `cards.title` and
 *   `link_target_title_cache` **from the same expression**, so they are
 *   byte-identical for every external paste and the front printed
 *   `example.com/docs/spec` twice.
 *
 * The card's title is on the prop already, so the rule can be stated where the
 * repeat happens: **the chrome never renders a line that repeats the card's own
 * title.** Passing a flag down instead would mean editing `CardTile.tsx`, and
 * the fix does not need it.
 *
 * ⚠ Trimmed and case-folded, because the two sides reach the same words through
 * different pipes — a catalogue literal and a server literal — and a comparison
 * that a stray capital defeats is a comparison that silently stops working
 * while both slots still read the same on screen.
 */
function repeatsCardTitle(line: string | null, cardTitle: string): boolean {
  return line !== null && line.trim().toLowerCase() === cardTitle.trim().toLowerCase();
}

export function LinkCardChrome({ card, onConvert }: LinkCardChromeProps) {
  const { t } = useInterfaceLanguage();
  const previewTitle = card.linkTargetUnavailable
    ? t('boardDashboard.link.unavailableTitle')
    : (card.linkTargetTitleCache ?? hostLabel(card.linkTargetUrl));
  const boardTitle = card.linkTargetBoardTitleCache;
  const subtitle = card.linkTargetUnavailable
    ? t('boardDashboard.link.unavailableSubtitle')
    : (boardTitle ?? hostLabel(card.linkTargetUrl));

  /**
   * ⚠ **DROPPED, NEVER SUBSTITUTED.** Nothing takes the place of a suppressed
   * line: the information is on the card front already, one element away, and
   * putting *something else* there is how a redacted surface grows a new
   * disclosure. An unavailable card therefore keeps its subtitle and its
   * hidden-target notice and loses only the duplicated heading — it is still
   * unmistakably the neutral state, and it is one line shorter, which the
   * density budget takes as a gain rather than a cost.
   *
   * ⚠⚠ **AND THE PREVIEW IS NEVER EMPTIED — `showSubtitle`'s second clause is
   * a floor, not a tidy-up.** Both lines repeat the card's title at once for a
   * bare-domain external paste: `https://example.com` stores
   * `linkCardTitleFromUrl` = `example.com` as BOTH the card's title and the
   * cached target title, and `hostLabel` yields the same word a third time. A
   * naive "drop every repeat" would then render this box as a border, an icon
   * and nothing else, which reads as a load failure rather than as a compact
   * preview. So when the heading goes, the subtitle stays whatever it says.
   */
  const previewTitleRepeats = repeatsCardTitle(previewTitle, card.title);
  const showPreviewTitle = !previewTitleRepeats;
  const showSubtitle = !repeatsCardTitle(subtitle, card.title) || previewTitleRepeats;

  return (
    <div className="pointer-events-none relative z-[3] flex flex-col gap-1 rounded-card border border-border bg-surface-raised/95 px-2 py-1 text-xs text-ink">
      <div className="flex min-w-0 items-start gap-1">
        <ExternalLink
          aria-hidden="true"
          className="mt-0.5 size-3 shrink-0 text-ink-muted"
        />
        <div className="min-w-0 flex-1">
          {showPreviewTitle ? (
            <div className="truncate font-medium">{previewTitle}</div>
          ) : null}
          {showSubtitle ? (
            <div className="truncate text-ink-muted">{subtitle}</div>
          ) : null}
        </div>
      </div>

      {card.linkTargetUnavailable ? (
        <div className="rounded-card border border-border-strong px-2 py-1 font-medium text-ink">
          {t('boardDashboard.link.hiddenTarget')}
        </div>
      ) : null}

      {onConvert ? (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          aria-label={t('boardDashboard.link.convert')}
          className="pointer-events-auto absolute right-1 top-1 z-10 size-7 p-0"
          iconStart={<FilePenLine aria-hidden="true" className="size-3.5" />}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onConvert();
          }}
        >
          <span className="sr-only">{t('boardDashboard.link.convert')}</span>
        </Button>
      ) : null}
    </div>
  );
}
